import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { LoginPage } from './pages/LoginPage';
import { OAuthCallback } from './pages/OAuthCallback';
import { UserManagementPage } from './pages/UserManagementPage';
import { ReferralQueuePage } from './pages/ReferralQueuePage';
import { AITriagingPage } from './pages/AITriagingPage';
import { SkillManagerPage } from './pages/SkillManagerPage';
import { DashboardPage } from './pages/DashboardPage';
import { PendingApprovalPage } from './pages/PendingApprovalPage';
import { DataPage } from './pages/DataPage';
import { SRQueuePage } from './pages/SRQueuePage';
import { SRIntegrationPage } from './pages/SRIntegrationPage';
import { ReferralSimulatorPage } from './pages/ReferralSimulatorPage';
import { ValidationHealthPage } from './pages/ValidationHealthPage';
import { AdminLayout } from './components/AdminLayout';
import { LoadingScreen } from './components/LoadingScreen';
import { AuthService } from './services/authService';
import { useCurrentUser } from './hooks/useCurrentUser';
import { buildLoginPath, getDefaultRouteForRole } from './utils/returnUrl';
import { SrNotificationsBell } from './components/sr/SrNotificationsBell';
import { ReferralNavSearch } from './components/ReferralNavSearch';
import './App.css';

const UserProfileError: React.FC<{ message?: string | null }> = ({ message }) => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <p>Unable to load your user profile.</p>
    {message && <p style={{ color: '#666' }}>{message}</p>}
    <button type="button" onClick={() => window.location.reload()}>
      Retry
    </button>
  </div>
);

function loginRedirect(location: { pathname: string; search: string }): React.ReactElement {
  const returnPath = `${location.pathname}${location.search}`;
  return <Navigate to={buildLoginPath(returnPath)} replace />;
}

// Protected Route component - for authenticated users
// Available for routes that need authentication but no specific role restrictions
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { user: currentUser, loading, error } = useCurrentUser();

  if (inProgress !== InteractionStatus.None) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return loginRedirect(location);
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <UserProfileError message={error} />;
  }

  if (currentUser.role?.toLowerCase() === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }

  return <>{children}</>;
};

// Pending Route component - for authenticated users (including pending)
const PendingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (inProgress !== InteractionStatus.None) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return loginRedirect(location);
  }

  return <>{children}</>;
};

// Admin Route component - for authenticated admin users only
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { user: currentUser, loading, error } = useCurrentUser();

  if (inProgress !== InteractionStatus.None) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return loginRedirect(location);
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <UserProfileError message={error} />;
  }

  if (currentUser.role?.toLowerCase() === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }

  const isAdmin = currentUser.role?.toLowerCase() === 'admin';

  if (!isAdmin) {
    return <Navigate to="/queue" replace />;
  }

  return <>{children}</>;
};

// Clinician Route component - for ADMIN and CLINICIAN roles only
const ClinicianRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { user: currentUser, loading, error } = useCurrentUser();

  if (inProgress !== InteractionStatus.None) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return loginRedirect(location);
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <UserProfileError message={error} />;
  }

  if (currentUser.role?.toLowerCase() === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }

  const userRole = currentUser.role?.toLowerCase() || '';
  const allowedRoles = ['admin', 'clinician'];

  if (!allowedRoles.includes(userRole)) {
    return <Navigate to="/queue" replace />;
  }

  return <>{children}</>;
};

// Non-Clinician Route component - blocks clinician access
const NonClinicianRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { user: currentUser, loading, error } = useCurrentUser();

  if (inProgress !== InteractionStatus.None) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return loginRedirect(location);
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <UserProfileError message={error} />;
  }

  if (currentUser.role?.toLowerCase() === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }

  const userRole = currentUser.role?.toLowerCase() || '';

  if (userRole === 'clinician') {
    return <Navigate to="/ai-triaging" replace />;
  }

  return <>{children}</>;
};

// Navigation component
const Navigation: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const { user: currentUser } = useCurrentUser();

  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    if (currentUser) {
      setUserName(currentUser.displayName);
      setUserRole(currentUser.role);
    } else if (!isAuthenticated) {
      setUserName('');
      setUserRole(null);
    }
  }, [currentUser, isAuthenticated]);

  const handleLogout = async () => {
    await AuthService.logout();
    setUserName('');
    setUserRole(null);
    navigate('/login');
  };

  const isAdmin = userRole?.toLowerCase() === 'admin';
  const isClinician = ['admin', 'clinician'].includes(userRole?.toLowerCase() || '');
  const isClinicianOnly = userRole?.toLowerCase() === 'clinician';
  const isPending = userRole?.toLowerCase() === 'pending';
  const showSrNotificationsBell = ['admin', 'reception', 'receptionist', 'pa', 'manager', 'viewer'].includes(
    userRole?.toLowerCase() || '',
  );

  return (
    <nav className="main-navigation">
      <div className="nav-brand">
        <Link to="/">
          <img src="/axis_website_logo_white.svg" alt="Axis" className="nav-brand-logo" />
        </Link>
      </div>
      <div className="nav-links">
        {isAuthenticated && !isPending && (
          <>
            {!isClinicianOnly && <Link to="/queue">Referral Queue</Link>}
            {isClinician && (
              <>
                <Link to="/ai-triaging">AI Triage</Link>
                <Link to="/skill-manager">Skill Manager</Link>
              </>
            )}
            {isAdmin && (
              <>
                <Link to="/admin">Admin</Link>
                <Link to="/users">User Management</Link>
              </>
            )}
          </>
        )}
        <div className="nav-right-section">
          {isAuthenticated ? (
            <div className="user-section">
              <ReferralNavSearch />
              <SrNotificationsBell enabled={showSrNotificationsBell} />
              <span className="user-name">{userName}</span>
              {userRole && (
                <span className={`user-role-badge ${userRole.toLowerCase()}`}>
                  {isPending ? 'Pending Approval' : userRole.toUpperCase()}
                </span>
              )}
              <button onClick={handleLogout} className="btn-logout">
                Logout
              </button>
            </div>
          ) : (
            <Link to="/login" className="btn-login-nav">
              Login
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

// Root redirect component
const RootRedirect: React.FC = () => {
  const location = useLocation();
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { user: currentUser, loading, error } = useCurrentUser();

  if (inProgress !== InteractionStatus.None) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return loginRedirect(location);
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <UserProfileError message={error} />;
  }

  const target = getDefaultRouteForRole(currentUser.role);
  return <Navigate to={target} replace />;
};

function App() {
  return (
    <Router>
      <div className="App">
        <Navigation />

        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route
            path="/pending-approval"
            element={
              <PendingRoute>
                <PendingApprovalPage />
              </PendingRoute>
            }
          />
          <Route
            path="/queue"
            element={
              <NonClinicianRoute>
                <ReferralQueuePage />
              </NonClinicianRoute>
            }
          />
          <Route
            path="/ai-triaging"
            element={
              <ClinicianRoute>
                <AITriagingPage />
              </ClinicianRoute>
            }
          />
          <Route
            path="/users"
            element={
              <AdminRoute>
                <UserManagementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/skill-manager"
            element={
              <ClinicianRoute>
                <SkillManagerPage />
              </ClinicianRoute>
            }
          />
          <Route path="/prompt-manager" element={<Navigate to="/skill-manager" replace />} />
          <Route
            path="/dashboard"
            element={
              <ClinicianRoute>
                <DashboardPage />
              </ClinicianRoute>
            }
          />
          <Route path="/data" element={<Navigate to="/admin/data" replace />} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<Navigate to="/admin/sr-queue" replace />} />
            <Route path="sr-queue" element={<SRQueuePage />} />
            <Route path="data" element={<DataPage />} />
            <Route path="sr-integration" element={<SRIntegrationPage />} />
            <Route path="simulate" element={<ReferralSimulatorPage />} />
            <Route path="validation-health" element={<ValidationHealthPage />} />
          </Route>
        </Routes>
      </div>
    </Router>
  );
}

export default App;
